import {
  Avatar,
  Badge,
  Box,
  HStack,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { RESEARCH_TYPES } from '../../utils/formatters';

const GRADIENT_BY_TYPE = {
  attribution: 'linear(to-br, purple.400, blue.500)',
  provenance: 'linear(to-br, blue.400, cyan.500)',
  identification: 'linear(to-br, teal.400, green.500)',
  technical: 'linear(to-br, cyan.400, blue.400)',
  heraldry: 'linear(to-br, orange.400, red.400)',
  paleography: 'linear(to-br, pink.400, purple.400)',
  archival: 'linear(to-br, yellow.400, orange.400)',
  other: 'linear(to-br, gray.400, gray.600)',
};

export const ResearcherCard = ({ researcher }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const primaryService = researcher.services?.[0] || 'other';
  const gradient = GRADIENT_BY_TYPE[primaryService] || GRADIENT_BY_TYPE.other;

  return (
    <Box
      as={Link}
      to={`/researchers/${researcher.id}`}
      bg={cardBg}
      rounded="xl"
      overflow="hidden"
      boxShadow="sm"
      borderWidth={1}
      borderColor={borderColor}
      _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)', textDecoration: 'none' }}
      transition="all 0.2s"
      display="block"
    >
      {/* Gradient header with avatar */}
      <Box h="100px" bgGradient={gradient} position="relative">
        <Avatar
          size="xl"
          src={researcher.avatarUrl}
          name={researcher.displayName}
          bg="orange.400"
          position="absolute"
          bottom="-32px"
          left="50%"
          transform="translateX(-50%)"
          border="4px solid"
          borderColor={cardBg}
        />
      </Box>

      {/* Card body */}
      <Box px={4} pt={10} pb={4}>
        <VStack spacing={2} align="center">
          <Text fontSize="md" fontWeight="semibold" noOfLines={1} textAlign="center">
            {researcher.displayName}
          </Text>

          <Text fontSize="sm" color="gray.500" noOfLines={2} textAlign="center" minH="40px">
            {researcher.tagline || researcher.bio}
          </Text>

          {/* Services badges */}
          <Wrap justify="center" spacing={1}>
            {(researcher.services || []).slice(0, 3).map((key) => {
              const type = RESEARCH_TYPES[key];
              if (!type) return null;
              return (
                <WrapItem key={key}>
                  <Badge
                    colorScheme={type.color}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    rounded="md"
                  >
                    {type.label}
                  </Badge>
                </WrapItem>
              );
            })}
            {(researcher.services || []).length > 3 && (
              <WrapItem>
                <Badge colorScheme="gray" fontSize="xs" px={2} py={0.5} rounded="md">
                  +{researcher.services.length - 3} more
                </Badge>
              </WrapItem>
            )}
          </Wrap>

          {/* Stats */}
          {researcher.reputation && (
            <HStack spacing={4} pt={1}>
              {researcher.reputation.completedBounties > 0 && (
                <Text fontSize="xs" color="gray.400">
                  {researcher.reputation.completedBounties} completed
                </Text>
              )}
              {researcher.reputation.rating > 0 && (
                <Text fontSize="xs" color="gray.400">
                  {researcher.reputation.rating.toFixed(1)} rating
                </Text>
              )}
            </HStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
